import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from './utils/apiService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Slider
} from '@mui/material';

const SelectorTemporalSlider = ({ onRangoChange, rangoActivo = [0, 100] }) => {
  const [periodosDisponibles, setPeriodosDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangoSlider, setRangoSlider] = useState(rangoActivo);

  const mesesMap = {
    'Enero': 1, 'Febrero': 2, 'Marzo': 3, 'Abril': 4, 'Mayo': 5, 'Junio': 6,
    'Julio': 7, 'Agosto': 8, 'Septiembre': 9, 'Octubre': 10, 'Noviembre': 11, 'Diciembre': 12
  };

  // Cargar períodos disponibles desde la API
  useEffect(() => {
    const cargarPeriodos = async () => {
      try {
        setLoading(true);
        // Obtener todos los períodos únicos de la base de datos
        const response = await apiGet('/periodos');
        if (Array.isArray(response)) {
          // Ordenar por año y mes
          const periodosOrdenados = response.sort((a, b) => {
            if (a.año !== b.año) {
              return parseInt(a.año) - parseInt(b.año);
            }
            return mesesMap[a.periodo] - mesesMap[b.periodo];
          });
          
          setPeriodosDisponibles(periodosOrdenados);
        }
      } catch (error) {
        console.error('Error cargando períodos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    cargarPeriodos();
  }, []);

  // Enviar el rango inicial cuando se cargan los períodos
  useEffect(() => {
    if (periodosDisponibles.length > 0) {
      const rangoInicial = convertirSliderARango(rangoSlider);
      onRangoChange(rangoInicial);
    }
  }, [periodosDisponibles, convertirSliderARango, onRangoChange, rangoSlider]);

  // Convertir índices del slider a rango de períodos
  const convertirSliderARango = useCallback((sliderValue) => {
    if (periodosDisponibles.length === 0) return {};
    
    // Si el slider está en [0, 100], devolver objeto vacío (todos los períodos)
    if (sliderValue[0] === 0 && sliderValue[1] === 100) {
      return {};
    }
    
    const totalPeriodos = periodosDisponibles.length;
    const indiceInicio = Math.floor((sliderValue[0] / 100) * (totalPeriodos - 1));
    const indiceFin = Math.floor((sliderValue[1] / 100) * (totalPeriodos - 1));
    
    const periodoInicio = periodosDisponibles[indiceInicio];
    const periodoFin = periodosDisponibles[indiceFin];
    
    return {
      mesInicio: periodoInicio?.periodo,
      anoInicio: periodoInicio?.año,
      mesFin: periodoFin?.periodo,
      anoFin: periodoFin?.año
    };
  }, [periodosDisponibles]);

  // Manejar cambio del slider con debounce para evitar demasiadas llamadas
  const handleSliderChange = useCallback((event, newValue) => {
    setRangoSlider(newValue);
  }, []);

  // Manejar el cambio final del slider (cuando se suelta)
  const handleSliderChangeCommitted = useCallback((event, newValue) => {
    const nuevoRango = convertirSliderARango(newValue);
    onRangoChange(nuevoRango);
  }, [convertirSliderARango, onRangoChange]);

  // Función para obtener las etiquetas del rango actual
  const obtenerEtiquetasRango = () => {
    if (periodosDisponibles.length === 0) {
      return { desde: 'Cargando...', hasta: 'Cargando...' };
    }
    
    // Si está en [0, 100], mostrar "Todos los períodos"
    if (rangoSlider[0] === 0 && rangoSlider[1] === 100) {
      const primerPeriodo = periodosDisponibles[0];
      const ultimoPeriodo = periodosDisponibles[periodosDisponibles.length - 1];
      return {
        desde: primerPeriodo ? `${primerPeriodo.periodo} ${primerPeriodo.año}` : 'Sin datos',
        hasta: ultimoPeriodo ? `${ultimoPeriodo.periodo} ${ultimoPeriodo.año}` : 'Sin datos'
      };
    }
    
    const totalPeriodos = periodosDisponibles.length;
    const indiceInicio = Math.floor((rangoSlider[0] / 100) * (totalPeriodos - 1));
    const indiceFin = Math.floor((rangoSlider[1] / 100) * (totalPeriodos - 1));
    
    const periodoInicio = periodosDisponibles[indiceInicio];
    const periodoFin = periodosDisponibles[indiceFin];
    
    return {
      desde: periodoInicio ? `${periodoInicio.periodo} ${periodoInicio.año}` : 'Sin datos',
      hasta: periodoFin ? `${periodoFin.periodo} ${periodoFin.año}` : 'Sin datos'
    };
  };

  // Crear marcas dinámicas para el slider
  const crearMarcasSlider = () => {
    if (periodosDisponibles.length === 0) return [];
    
    const marcas = [];
    const totalPeriodos = periodosDisponibles.length;
    
    // Marcar inicio, cuartos y final
    const posiciones = [0, 25, 50, 75, 100];
    
    posiciones.forEach(porcentaje => {
      const indice = Math.floor((porcentaje / 100) * Math.max(0, totalPeriodos - 1));
      const periodo = periodosDisponibles[indice];
      
      if (periodo) {
        marcas.push({
          value: porcentaje,
          label: porcentaje === 0 ? `${periodo.periodo} ${periodo.año}` :
                 porcentaje === 100 ? `${periodo.periodo} ${periodo.año}` :
                 `${porcentaje}%`
        });
      }
    });
    
    return marcas;
  };

  const marcasSlider = crearMarcasSlider();
  const etiquetasRango = obtenerEtiquetasRango();

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📅 Cargando períodos disponibles...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (periodosDisponibles.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ⚠️ No se encontraron períodos disponibles
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          📅 Ajustar rango temporal
        </Typography>
        
        {/* Información del rango actual */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Período seleccionado:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Desde: <span style={{color: '#1976d2'}}>{etiquetasRango.desde}</span> • 
            Hasta: <span style={{color: '#1976d2'}}>{etiquetasRango.hasta}</span>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total de períodos disponibles: {periodosDisponibles.length}
          </Typography>
        </Box>

        {/* Slider de rango */}
        <Box sx={{ px: 2 }}>
          <Typography variant="body2" gutterBottom>
            Desliza para ajustar el rango:
          </Typography>
          <Slider
            value={rangoSlider}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => {
              const totalPeriodos = periodosDisponibles.length;
              if (totalPeriodos === 0) return `${value}%`;
              
              const indice = Math.floor((value / 100) * Math.max(0, totalPeriodos - 1));
              const periodo = periodosDisponibles[indice];
              return periodo ? `${periodo.periodo} ${periodo.año}` : `${value}%`;
            }}
            min={0}
            max={100}
            step={Math.max(1, Math.floor(100 / Math.max(1, periodosDisponibles.length)))}
            marks={marcasSlider}
            sx={{ mt: 2, mb: 2 }}
          />
        </Box>

        {/* Botones de acceso rápido */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              const newValue = [0, 100];
              setRangoSlider(newValue);
              handleSliderChangeCommitted(null, newValue);
            }}
          >
            📊 Todo el período
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              const newValue = [75, 100];
              setRangoSlider(newValue);
              handleSliderChangeCommitted(null, newValue);
            }}
          >
            📈 Últimos meses
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              const newValue = [0, 25];
              setRangoSlider(newValue);
              handleSliderChangeCommitted(null, newValue);
            }}
          >
            📉 Primeros meses
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              const newValue = [40, 60];
              setRangoSlider(newValue);
              handleSliderChangeCommitted(null, newValue);
            }}
          >
            📊 Período medio
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SelectorTemporalSlider;